/**
 * Shared design tokens for the AI Agent screen.
 * Import this everywhere instead of defining local VG/C objects.
 */

// ── Brand Colors ────────────────────────────────────────────────────────────
export const brand = {
  coral: '#FF7E67',
  pink: '#FF4D8D',
  gradient: 'linear-gradient(to right, #FF7E67, #FF4D8D)',
};

// ── Surfaces & Glass (aligned with vivid-glass-design-system.md) ────────────
export const surface = {
  bg: 'rgba(12,12,18,0.98)',
  glass: 'rgba(255,255,255,0.06)',
  glassBorder: 'rgba(255,255,255,0.15)',
  glassBorderHover: 'rgba(255,255,255,0.25)',
  card: 'rgba(255,255,255,0.06)',
  cardBorder: 'rgba(255,255,255,0.12)',
  cardBorderStrong: 'rgba(255,255,255,0.18)',
  cardHover: 'rgba(255,255,255,0.09)',
};

// ── Text ────────────────────────────────────────────────────────────────────
export const text = {
  primary: '#FFFFFF',
  secondary: '#e8e8f0',
  sub: '#94A3B8',
  muted: '#64748B',
  dim: 'rgba(255,255,255,0.25)',
};

// ── Semantic Colors ─────────────────────────────────────────────────────────
export const semantic = {
  info: '#38BDF8',
  infoBg: 'rgba(56,189,248,0.1)',
  success: '#4ade80',
  successBg: 'rgba(74,222,128,0.1)',
  warning: '#fbbf24',
  warningBg: 'rgba(251,191,36,0.1)',
  error: '#f87171',
  errorBg: 'rgba(248,113,113,0.1)',
  accent: '#818cf8',
  accentBg: 'rgba(129,140,248,0.1)',
  purple: '#c084fc',
  purpleBg: 'rgba(192,132,252,0.1)',
  blue: '#60a5fa',
  blueBg: 'rgba(96,165,250,0.1)',
  green: '#34d399',
  greenBg: 'rgba(52,211,153,0.1)',
};

// ── Typography ──────────────────────────────────────────────────────────────
export const mono = "'JetBrains Mono', 'Fira Code', 'Courier New', monospace";

// ── Shared Style Patterns ───────────────────────────────────────────────────
export const patterns = {
  glassCard: {
    background: surface.card,
    border: `1px solid ${surface.cardBorder}`,
    borderRadius: 12,
  },
  userBubble: {
    background: 'rgba(255,126,103,0.12)',
    border: '1px solid rgba(255,126,103,0.25)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 2px 16px rgba(0,0,0,0.15)',
    color: '#FFFFFF',
    borderRadius: '18px 18px 4px 18px',
    padding: '10px 15px',
  },
  assistantBubble: {
    background: surface.card,
    border: `1px solid ${surface.cardBorder}`,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 2px 16px rgba(0,0,0,0.1)',
    color: '#dddada',
    borderRadius: '4px 18px 18px 18px',
    padding: '12px 16px',
  },
};

// ── Card Type Tokens (semantic card backgrounds per tool/message type) ──────
export const cardTypes = {
  approval: {
    bg: 'rgba(74,222,128,0.05)',
    border: 'rgba(74,222,128,0.15)',
    borderHover: 'rgba(74,222,128,0.3)',
    accent: '#4ade80',
  },
  generation: {
    bg: 'rgba(255,126,103,0.04)',
    border: 'rgba(255,126,103,0.12)',
    borderHover: 'rgba(255,126,103,0.25)',
    accent: brand.coral,
  },
  copy: {
    bg: 'rgba(192,132,252,0.05)',
    border: 'rgba(192,132,252,0.15)',
    borderHover: 'rgba(192,132,252,0.3)',
    accent: '#c084fc',
  },
  analysis: {
    bg: 'rgba(56,189,248,0.04)',
    border: 'rgba(56,189,248,0.12)',
    borderHover: 'rgba(56,189,248,0.25)',
    accent: '#38BDF8',
  },
  system: {
    bg: 'rgba(255,255,255,0.02)',
    border: 'rgba(255,255,255,0.08)',
    accent: 'rgba(255,255,255,0.15)',
  },
};

// ── Mobile Layout Constants ─────────────────────────────────────────────────
export const mobile = {
  headerHeight: 52,
  sheetRadius: 20,
  drawerWidth: 300,
  touchTarget: 44,
};

// ── Backward-compatible aliases ─────────────────────────────────────────────
// Use these during migration from VG/C. Remove once all files use the new tokens.
export const VG = {
  coral: brand.coral,
  pink: brand.pink,
  info: semantic.info,
  gradient: brand.gradient,
  glass: surface.glass,
  glassBorder: surface.glassBorder,
  glassBorderHover: surface.glassBorderHover,
  textPrimary: text.primary,
  textSub: text.sub,
  textMuted: text.muted,
};

export const C = {
  bg: surface.bg,
  surface: surface.card,
  surfaceHover: surface.cardHover,
  border: surface.cardBorder,
  borderStrong: surface.cardBorderStrong,
  text: text.secondary,
  textMuted: 'rgba(255,255,255,0.45)',
  textDim: text.dim,
  accent: semantic.accent,
  accentBg: semantic.accentBg,
  green: semantic.green,
  greenBg: semantic.greenBg,
  yellow: semantic.warning,
  yellowBg: semantic.warningBg,
  red: semantic.error,
  redBg: semantic.errorBg,
  blue: semantic.blue,
  blueBg: semantic.blueBg,
  purple: semantic.purple,
  purpleBg: semantic.purpleBg,
  mono,
};
